import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Alert,
  Box,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useTeamDetails } from "../hooks/useTeams";
import TeamLogo from "../components/TeamLogo";
import PlayerNameCell from "../components/PlayerNameCell";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { buildTeamRosterRows } from "../utils/teamTableRows";
import { formatPlayerName } from "../utils/textUtils";
import { QUERY_PAGE_SIZE_OPTIONS } from "../utils/queryBuilder";
import {
  NAME_SORT_ORDER,
  getResultsGridSx,
  sortedClassFor,
} from "../utils/resultsGrid";

const TeamDetails = () => {
  const { teamName } = useParams();
  const navigate = useNavigate();
  const decodedName = decodeURIComponent(teamName || "");
  const { team, isLoading, error } = useTeamDetails(decodedName);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortModel, setSortModel] = useState([
    { field: "playerName", sort: "asc" },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const sortedField = sortModel[0]?.field;

  const filteredPlayers = useMemo(() => {
    if (!team?.players) return [];
    if (!searchTerm.trim()) return team.players;

    return team.players.filter((player) => {
      const playerName = formatPlayerName(player);
      const playerId = player?.playerId || "";
      const searchLower = searchTerm.toLowerCase();
      return (
        playerName.toLowerCase().includes(searchLower) ||
        playerId.toLowerCase().includes(searchLower)
      );
    });
  }, [team?.players, searchTerm]);

  const rows = useMemo(
    () => buildTeamRosterRows(filteredPlayers),
    [filteredPlayers]
  );

  const handlePlayerClick = (playerId) => {
    navigate(`/app/player/${playerId}`);
  };

  const columns = useMemo(
    () => [
      {
        field: "playerName",
        headerName: "Player",
        flex: 1.2,
        minWidth: 220,
        headerClassName: sortedClassFor("playerName", sortedField),
        cellClassName: sortedClassFor("playerName", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => (
          <PlayerNameCell
            playerId={params.row.playerId}
            name={params.value}
            onPlayerClick={handlePlayerClick}
          />
        ),
      },
      {
        field: "playerId",
        headerName: "Player ID",
        width: 160,
        headerClassName: sortedClassFor("playerId", sortedField),
        cellClassName: sortedClassFor("playerId", sortedField),
        sortingOrder: NAME_SORT_ORDER,
      },
    ],
    [sortedField]
  );

  const displayName = team?.name || decodedName;

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading team data
        </Alert>
      </Container>
    );
  }

  if (!isLoading && !team) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Team not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/app/teams")}
        sx={{ mb: 3 }}
      >
        Back to Teams
      </Button>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          mb: 1,
        }}
      >
        <TeamLogo teamName={displayName} size={56} alt={`${displayName} logo`} />
        <Typography variant="h3" component="h1">
          {displayName}
        </Typography>
      </Box>

      <Typography
        variant="h6"
        gutterBottom
        align="center"
        color="text.secondary"
      >
        Team Details
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary">
                  {team?.totalPlayers || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Players
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box textAlign="center">
                <Typography variant="h4" color="secondary">
                  {filteredPlayers.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? "Filtered Players" : "Active Players"}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h5">Players</Typography>
            <TextField
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Paper sx={{ overflow: "hidden" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isLoading}
              pageSizeOptions={QUERY_PAGE_SIZE_OPTIONS}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              disableRowSelectionOnClick
              disableColumnMenu
              sortingMode="client"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              rowHeight={52}
              sx={getResultsGridSx()}
            />
          </Paper>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TeamDetails;
